import type { SessionResponse } from "@frontier/protocol/contracts";
import type { AuthApi } from "./auth-session";
import type { AuthStorage } from "./auth-storage";
import { ApiError, api } from "./session-api";

export interface PlatformAuthAdapter {
  readonly provider: string;
  readonly hasLaunchProof: boolean;
  readonly messages: {
    readonly missingProof: string;
    readonly foreignSession: string;
    readonly unconfirmedIdentity: string;
  };
  initialize(): Promise<void>;
  exchange(guestToken?: string): Promise<SessionResponse>;
  continueLogin(): Promise<void>;
}

type SessionApi = Pick<typeof api, "session" | "profile">;

/** Shared ownership and guest transfer; provider proof and SDK stay in the adapter. */
export class PlatformSession {
  constructor(
    private readonly adapter: PlatformAuthAdapter,
    private readonly storage: AuthStorage,
    private readonly requests: SessionApi = api,
  ) {}

  async session(token: string): Promise<SessionResponse> {
    await this.adapter.initialize();
    if (!this.adapter.hasLaunchProof) {
      if (!token) throw new Error(this.adapter.messages.missingProof);
      const restored = await this.requests.session(token);
      if (this.owns(restored)) return restored;
      throw new Error(this.adapter.messages.foreignSession);
    }
    const guest = token ? null : await this.currentGuest();
    const result = await this.adapter.exchange(guest?.token);
    if (!this.owns(result))
      throw new Error(this.adapter.messages.unconfirmedIdentity);
    if (
      guest &&
      result.profile.playerId === guest.playerId &&
      this.storage.getItem("frontier.token") === guest.token &&
      !this.storage.getItem("frontier.signed-out")
    )
      this.storage.setItem("frontier.token", result.token);
    return result;
  }

  private owns(session: SessionResponse): boolean {
    const account = session.profile.account;
    return (
      account !== null &&
      "provider" in account &&
      account.provider === this.adapter.provider
    );
  }

  private async currentGuest(): Promise<{
    token: string;
    playerId: string;
  } | null> {
    if (this.storage.getItem("frontier.signed-out")) return null;
    const token = this.storage.getItem("frontier.token");
    if (!token) return null;
    try {
      const profile = await this.requests.profile(token);
      return profile.account ? null : { token, playerId: profile.playerId };
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  }

  continueLogin(): Promise<void> {
    return this.adapter.continueLogin();
  }

  authApi(): AuthApi {
    return { ...api, session: (token) => this.session(token) };
  }
}
